/**
 * Per-chain address checksum verification — TypeScript mirror of the backend
 * `app/analysis/address_checksum.py`.
 *
 * Why this exists: the omnibar resolves an address's chain entirely in the
 * browser, BEFORE any backend call (a DOGE search fires zero requests). The
 * prefix+length detector in `chainDetect.ts` is the right *policy* for typed
 * input, but several chains share the base58 alphabet and overlapping lengths
 * (DOGE/BTC/LTC/DASH/FIRO at 34 chars; SOL's 32–44 catch-all swallows all of
 * them). Prefix ordering is a heuristic, not a proof — every new prefix that
 * collides re-opens the same "X mislabeled as Y" bug class (the DOGE→SOL bug
 * was the latest instance).
 *
 * A base58check address carries a 4-byte sha256d checksum over a version-byte
 * prefix. Decoding it and reading the version byte identifies the chain
 * *definitively*, and the checksum proves the string is a real address of that
 * structure rather than a look-alike. Solana addresses are raw 32-byte ed25519
 * public keys (no checksum) — verifying the bytes encode a valid curve point
 * separates them from base58check chains and from junk.
 *
 * Pure, synchronous, zero-dependency: a self-contained sha256 + base58 decoder
 * + ed25519 curve check. Synchronous because the omnibar badge re-runs on every
 * keystroke; an async Web Crypto digest would not fit that path. Mirrors the
 * backend's stdlib-only philosophy so the two stay portable and in parity.
 */

// ── sha256 (synchronous, pure) ──────────────────────────────────────────────
// Standard FIPS-180-4 implementation over bytes. Used only for the 4-byte
// base58check trailer, so throughput is irrelevant — clarity wins.

const _K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const _rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

function sha256(msg: Uint8Array): Uint8Array {
    const h = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);

    // Pre-processing: pad to a multiple of 64 bytes with 0x80, zeros, and the
    // 64-bit big-endian bit length.
    const bitLen = msg.length * 8;
    const withPad = (((msg.length + 8) >> 6) + 1) << 6; // next multiple of 64 leaving room for length
    const data = new Uint8Array(withPad);
    data.set(msg);
    data[msg.length] = 0x80;
    // 64-bit length; JS bit ops are 32-bit, so write the low 32 bits (addresses
    // are tiny — the high word is always 0 here).
    const dv = new DataView(data.buffer);
    dv.setUint32(withPad - 4, bitLen >>> 0, false);
    dv.setUint32(withPad - 8, Math.floor(bitLen / 0x100000000) >>> 0, false);

    const w = new Uint32Array(64);
    for (let off = 0; off < data.length; off += 64) {
        for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4, false);
        for (let i = 16; i < 64; i++) {
            const s0 = _rotr(w[i - 15], 7) ^ _rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = _rotr(w[i - 2], 17) ^ _rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, hh] = h;
        for (let i = 0; i < 64; i++) {
            const S1 = _rotr(e, 6) ^ _rotr(e, 11) ^ _rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const t1 = (hh + S1 + ch + _K[i] + w[i]) >>> 0;
            const S0 = _rotr(a, 2) ^ _rotr(a, 13) ^ _rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (S0 + maj) >>> 0;
            hh = g; g = f; f = e; e = (d + t1) >>> 0;
            d = c; c = b; b = a; a = (t1 + t2) >>> 0;
        }
        h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0; h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0; h[5] = (h[5] + f) >>> 0; h[6] = (h[6] + g) >>> 0; h[7] = (h[7] + hh) >>> 0;
    }

    const out = new Uint8Array(32);
    const odv = new DataView(out.buffer);
    for (let i = 0; i < 8; i++) odv.setUint32(i * 4, h[i], false);
    return out;
}

const sha256d = (msg: Uint8Array): Uint8Array => sha256(sha256(msg));

// ── base58 ──────────────────────────────────────────────────────────────────
// Bitcoin-family alphabet (no 0/O/I/l). Used by BTC, LTC, DOGE, DASH, FIRO,
// TRX, ZEC-transparent, and SOL. XRP uses the same 58 glyphs in a different
// order — decoding XRP with the BTC table yields wrong bytes, so the checksum
// would never match; keep the tables independent.
const _BTC_B58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const _XRP_B58 = 'rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz';

/**
 * Decode a base58 string to bytes using the given alphabet. Returns null when
 * any character is outside the alphabet — that lets the caller distinguish
 * "not valid base58 for this chain" from "valid base58 but checksum failed".
 */
function b58decode(s: string, alphabet: string): Uint8Array | null {
    if (!s) return null;
    const map: Record<string, number> = {};
    for (let i = 0; i < alphabet.length; i++) map[alphabet[i]] = i;

    // Treat the string as a big-endian base-58 integer (BigInt for exactness).
    let n = 0n;
    for (const ch of s) {
        const v = map[ch];
        if (v === undefined) return null;
        n = n * 58n + BigInt(v);
    }

    // Big int → big-endian bytes.
    const bytes: number[] = [];
    while (n > 0n) {
        bytes.unshift(Number(n & 0xffn));
        n >>= 8n;
    }

    // Leading-zero preservation: each leading alphabet[0] glyph is one 0x00 byte.
    let leadingZeros = 0;
    for (const ch of s) {
        if (ch === alphabet[0]) leadingZeros++;
        else break;
    }
    return new Uint8Array([...new Array(leadingZeros).fill(0), ...bytes]);
}

/**
 * Verify a base58check string and return its decoded payload (version byte(s)
 * + hash, excluding the 4-byte checksum) when the checksum matches. Returns
 * null on a bad alphabet, too-short input, or checksum mismatch.
 */
function verifyBase58Check(addr: string, alphabet: string): Uint8Array | null {
    const decoded = b58decode(addr, alphabet);
    if (!decoded || decoded.length < 5) return null;
    const payload = decoded.subarray(0, decoded.length - 4);
    const checksum = decoded.subarray(decoded.length - 4);
    const expected = sha256d(payload).subarray(0, 4);
    for (let i = 0; i < 4; i++) if (checksum[i] !== expected[i]) return null;
    return payload;
}

// ── ed25519 curve point check (for Solana) ──────────────────────────────────
// A Solana address IS a 32-byte ed25519 public key. RFC 8032 §5.1.3 decoding:
// a valid encoding requires y < p and x² = (y²−1)/(d·y²+1) mod p to have a
// square root. A random 32-byte string is a valid point ~50% of the time, so
// this halves the false-positive rate before any on-chain lookup.
const _P = (1n << 255n) - 19n;
const _D = (-121665n * _modpow(121666n, _P - 2n, _P)) % _P;
const _SQRT_M1 = _modpow(2n, (_P - 1n) / 4n, _P);

function _modpow(base: bigint, exp: bigint, mod: bigint): bigint {
    let result = 1n;
    let b = ((base % mod) + mod) % mod;
    let e = exp;
    while (e > 0n) {
        if (e & 1n) result = (result * b) % mod;
        b = (b * b) % mod;
        e >>= 1n;
    }
    return result;
}

function isEd25519CurvePoint(b: Uint8Array): boolean {
    if (b.length !== 32) return false;
    // Little-endian decode.
    let n = 0n;
    for (let i = 31; i >= 0; i--) n = (n << 8n) | BigInt(b[i]);
    const sign = (n >> 255n) & 1n;
    const y = n & ((1n << 255n) - 1n);
    if (y >= _P) return false;

    const yy = (y * y) % _P;
    const u = (yy - 1n + _P) % _P;
    const v = (((_D * yy) % _P) + 1n) % _P;
    if (v === 0n) return false;
    const x2 = (u * _modpow(v, _P - 2n, _P)) % _P;
    if (x2 === 0n) return sign === 0n; // point (0, ±1): valid only when sign bit clear
    let x = _modpow(x2, (_P + 3n) / 8n, _P);
    if ((x * x - x2) % _P !== 0n) x = (x * _SQRT_M1) % _P;
    if ((((x * x - x2) % _P) + _P) % _P !== 0n) return false;
    return true;
}

// ── version byte → chain identification ─────────────────────────────────────
// First payload byte of a base58check address encodes the network + script
// type. This table is the definitive disambiguator for the base58 family.
// Where two chains genuinely share a version byte (e.g. BTC P2SH 0x05 vs old
// LTC P2SH 0x05) the value maps to the more common chain and the caller's
// prefix heuristic can still refine — but those are real on-chain collisions,
// not detector bugs.
const _VERSION_TO_CHAIN: Record<number, string> = {
    0x00: 'BTC',  // BTC P2PKH ('1')
    0x05: 'BTC',  // BTC P2SH  ('3')   — old LTC P2SH also 0x05
    0x30: 'LTC',  // LTC P2PKH ('L')
    0x32: 'LTC',  // LTC P2SH  ('M')
    0x1e: 'DOGE', // DOGE P2PKH ('D')
    0x16: 'DOGE', // DOGE P2SH  ('9'/'A')
    0x4c: 'DASH', // DASH P2PKH ('X')
    0x10: 'DASH', // DASH P2SH  ('7')
    0x52: 'FIRO', // FIRO P2PKH ('a')
    0x41: 'TRX',  // TRON ('T')
};

/**
 * Identify the chain of a base58 address by checksum + version byte. This is a
 * *confirming* classifier: it returns a single-element list only when a
 * checksum verifies, so the caller can trust the result; otherwise it returns
 * an empty list and the caller falls back to its prefix/length heuristic. It
 * never throws and never guesses.
 *
 *   - Bitcoin-family base58check (BTC/LTC/DOGE/DASH/FIRO/TRX) → version byte
 *   - Zcash transparent (2-byte version 0x1c 0xb8/0xbd)       → 'ZEC'
 *   - XRP (custom alphabet, classic 'r' address)              → 'XRP'
 *   - Solana ed25519 public key (32 bytes, on-curve)          → 'SOL'
 */
export function identifyBase58Chain(address: string | null | undefined): string[] {
    if (!address) return [];
    const s = address.trim();
    if (!s) return [];

    // 1. Bitcoin-family base58check.
    const payload = verifyBase58Check(s, _BTC_B58);
    if (payload && payload.length >= 1) {
        // Zcash transparent uses a 2-byte version prefix.
        if (payload.length >= 2 && payload[0] === 0x1c && (payload[1] === 0xb8 || payload[1] === 0xbd)) {
            return ['ZEC'];
        }
        const chain = _VERSION_TO_CHAIN[payload[0]];
        if (chain) return [chain];
        // Valid checksum but unknown version byte — still a real base58check
        // address, just one we don't have a chain for. Let the caller's
        // heuristic decide rather than asserting a wrong chain.
        return [];
    }

    // 2. XRP — only worth trying for the 'r' classic form (custom alphabet).
    if (s[0] === 'r') {
        const xrpPayload = verifyBase58Check(s, _XRP_B58);
        if (xrpPayload && xrpPayload.length >= 1 && xrpPayload[0] === 0x00) return ['XRP'];
    }

    // 3. Solana — 32-byte ed25519 public key, no checksum.
    const raw = b58decode(s, _BTC_B58);
    if (raw && raw.length === 32 && isEd25519CurvePoint(raw)) return ['SOL'];

    return [];
}

// Exported for unit tests / parity checks. Not part of the public detector API.
export const __internals = { sha256, sha256d, b58decode, verifyBase58Check, isEd25519CurvePoint };
